import {
  TActualType,
  TBackgroundEventsSyncingDB,
  TBctxItemAndSyncedStateSize,
  TBctxItemWithoutSize,
  TBctxItemWithSize,
  TBctxKeyToBctxItemWithSizeMap,
  TBCTXKeysWithoutSize,
  TBctxLevelSyncedState,
  TBulkEvent,
  TBulkEventWithoutStatus,
  TClientWork,
  TEventBctxDetails,
  TLineMapItem,
  TRESPONSE_AppStateResponseForBackedUpBctx,
  TRestoreBackedUpBctx,
  TSelectedStationIdAndShiftId,
  TSingleCountEvent,
  TSizeMap,
  TUserActualsDB,
  TNewBctxItemInPOSplit,
  TBctxMinMaxDateTime,
} from '@monorepo/types';
import produce from 'immer';
import { BCTXComputationHelper } from './bctx-computation-helper';
import { DEFAULT_SELECTED_BCTX_INFO } from '@monorepo/core-data';

export const PerPieceComputationHelper = {
  getMergedProductTypeStyleDBOnBctxLoad: (
    currentDB: TUserActualsDB['dailyCache'],
    selectedStationAndShiftId: TSelectedStationIdAndShiftId,
    lineMapItem: TLineMapItem,
  ) => {
    const { stationId } = selectedStationAndShiftId;

    currentDB = produce(currentDB, (draft) => {
      if (!draft.ppLineMap[stationId]) {
        draft.ppLineMap[stationId] = {
          lineInfo: {},
          ps: [],
          selectedBctxInfo: DEFAULT_SELECTED_BCTX_INFO,
        };
      }

      const productStyleArray: TBctxItemWithoutSize[][] = [];

      Object.entries(lineMapItem.bctxWipMap).forEach(([bctxkey, { bctxItemListWithoutSize, sizeToWipMap }]) => {
        const productTypeStyle = bctxItemListWithoutSize.filter(
          (bctxItem) => bctxItem.k === 'product_type' || bctxItem.k === 'style',
        );
        productStyleArray.push(productTypeStyle);
      });

      draft.ppLineMap[stationId].ps = productStyleArray;
    });

    return currentDB;
  },
  getMergedDBOnRestoreForBackedUpBctxPerPiece: (
    selectedStationAndShiftId: TSelectedStationIdAndShiftId,
    currentDB: TUserActualsDB['dailyCache'],
    appStateResponse: TRESPONSE_AppStateResponseForBackedUpBctx,
  ) => {
    const { stationId, shiftId } = selectedStationAndShiftId;

    let aggregatedLineMap = BCTXComputationHelper.createInitialClientWork();

    const bctxItemAndSyncedStateSizeList: TBctxItemAndSyncedStateSize[] =
      appStateResponse.appSyncedState && appStateResponse.appSyncedState.bctxLevelSyncedStates
        ? appStateResponse.appSyncedState.bctxLevelSyncedStates
        : [];

    bctxItemAndSyncedStateSizeList.forEach((bctxItemAndSyncedStateSizeItem) => {
      bctxItemAndSyncedStateSizeItem.sizes.forEach((sizeData) => {
        aggregatedLineMap = PerPieceComputationHelper.getAggregatedClientWorkWithoutToBeRectifiedCounts(
          aggregatedLineMap,
          sizeData,
        );
      });
    });

    currentDB = produce(currentDB, (draft) => {
      if (!draft.ppLineMap[stationId]) {
        draft.ppLineMap[stationId] = {
          lineInfo: {
            [shiftId]: {
              bctx: [],
              clientWork: aggregatedLineMap,
            },
          },
          ps: [],
          selectedBctxInfo: DEFAULT_SELECTED_BCTX_INFO,
        };
      } else if (!draft.ppLineMap[stationId].lineInfo[shiftId]) {
        draft.ppLineMap[stationId].lineInfo[shiftId] = {
          bctx: [],
          clientWork: aggregatedLineMap,
        };
      }
    });
    return currentDB;
  },
  getAggregatedClientWorkWithoutToBeRectifiedCounts: (
    client_work: TClientWork,
    bctxLevelSyncedState: TBctxLevelSyncedState,
  ): TClientWork => {
    return produce(client_work, (draft_client_work) => {
      draft_client_work['CHECK_IN'].actuals += bctxLevelSyncedState.actualsCount.checkin;
      draft_client_work['FTT'].actuals += bctxLevelSyncedState.actualsCount.ftt;
      draft_client_work['REJECTED'].actuals += bctxLevelSyncedState.actualsCount.reject;
      draft_client_work['REJECTED_FROM_DEFECTIVE'].actuals += bctxLevelSyncedState.actualsCount.reject_r;
      draft_client_work['REWORKED_FROM_DEFECTIVE'].actuals += bctxLevelSyncedState.actualsCount.rectified;
      draft_client_work['DEFECTIVE'].actuals += bctxLevelSyncedState.actualsCount.defectives;
    });
  },

  getAllBctxItemsAcrossAllLinesAsRecordPerPiece: (ppLineMap: TUserActualsDB['dailyCache']['ppLineMap']) => {
    const aggregatedProductStyleBctxData: TBctxItemWithoutSize[][] = [];
    Object.values(ppLineMap).forEach((bctxItem) => {
      aggregatedProductStyleBctxData.push(...bctxItem.ps);
    });

    return aggregatedProductStyleBctxData;
  },

  mergeUserAggregatedLineMapWithBgAggregatedLineMap(
    userAggLineMap: TUserActualsDB['dailyCache']['ppLineMap'],
    bgAggLineMap: TBackgroundEventsSyncingDB['dailyCache']['ppLineMap'],
  ): TUserActualsDB['dailyCache']['ppLineMap'] {
    Object.keys(bgAggLineMap).forEach((line) => {
      Object.keys(bgAggLineMap[line].lineInfo).forEach((shift) => {
        userAggLineMap = produce(userAggLineMap, (draft) => {
          Object.entries(bgAggLineMap[line].lineInfo[shift].clientWork).forEach(([actualType, clientWork]) => {
            if (clientWork.actuals !== 0 && draft[line] && draft[line].lineInfo[shift]) {
              draft[line].lineInfo[shift].clientWork[actualType as TActualType].actuals += clientWork.actuals;
            }
          });
        });
      });
    });

    return userAggLineMap;
  },
  updateActualsForBulkEventsForAllSingleEventsPerPiece(
    currentDB: TUserActualsDB,
    bulkEvents: TBulkEventWithoutStatus[],
    orderKeys: TBCTXKeysWithoutSize[],
  ) {
    bulkEvents.forEach((bulkEvent) => {
      if (bulkEvent.type === 'FORWARD_BULK_EVENT' || bulkEvent.type === 'UNDO_BULK_EVENT') {
        const stationDetails = bulkEvent.metadata.stationDetails;
        const direction = 1;
        bulkEvent.events.forEach((event) => {
          const bctxDetails = event.eventInfo.bctxDetails;

          const modifiedBctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
            orderKeys,
            bctxDetails.businessContextDetailsWithoutSize,
          );
          const updatedbctxMap = PerPieceComputationHelper.backFillBctxInUserPerPieceLineMap(
            bctxDetails,
            currentDB.dailyCache.ppLineMap,
            stationDetails.subject.subject,
            stationDetails.shift.id,
            modifiedBctxKey,
            orderKeys,
          );
          currentDB = produce(currentDB, (draft) => {
            draft.dailyCache.ppLineMap = updatedbctxMap;
          });
          currentDB = produce(currentDB, (draft) => {
            const client_work =
              draft.dailyCache.ppLineMap[stationDetails.subject.subject].lineInfo[stationDetails.shift.id].clientWork;
            switch (event.actionType) {
              case 'DEFECTIVE':
              case 'UNDO_DEFECTIVE':
                if (event.subType === 'DEFECTIVE' || event.subType === 'UNDO_DEFECTIVE') {
                  client_work.DEFECTIVE.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.IN_PROGRESS.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.TO_BE_RECTIFIED.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                  return;
                } else return;
              case 'CHECK_IN':
              case 'UNDO_CHECK_IN':
                client_work.CHECK_IN.actuals +=
                  bctxDetails.count * (event.actionType === 'UNDO_CHECK_IN' ? -1 : 1) * direction;
                return;
              case 'FTT':
              case 'UNDO_FTT':
                client_work.FTT.actuals += bctxDetails.count * (event.actionType === 'UNDO_FTT' ? -1 : 1) * direction;
                return;
              case 'REJECTED':
              case 'UNDO_REJECTED':
                client_work.REJECTED.actuals +=
                  bctxDetails.count * (event.actionType === 'UNDO_REJECTED' ? -1 : 1) * direction;
                return;
              case 'REWORKED_FROM_DEFECTIVE':
              case 'UNDO_REWORKED_FROM_DEFECTIVE': {
                const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                client_work.REWORKED_FROM_DEFECTIVE.actuals +=
                  bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                client_work.TO_BE_RECTIFIED.actuals -=
                  bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                if (isToday) {
                  client_work.IN_PROGRESS.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                }
                return;
              }
              case 'REJECTED_FROM_DEFECTIVE':
              case 'UNDO_REJECTED_FROM_DEFECTIVE': {
                const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                client_work.REJECTED_FROM_DEFECTIVE.actuals +=
                  bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                client_work.TO_BE_RECTIFIED.actuals -=
                  bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                if (isToday) {
                  client_work.IN_PROGRESS.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                }
                return;
              }
            }
          });
        });
      }
    });
    return currentDB;
  },
  getPerPieceLineMapBctxKeyMapper(
    bctxItemWithSize: TBctxItemWithSize[],
    orderKeys: TBCTXKeysWithoutSize[],
  ): TBctxKeyToBctxItemWithSizeMap {
    const bctxItemMap: TBctxKeyToBctxItemWithSizeMap = {};
    bctxItemWithSize.forEach((bctxItem) => {
      const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(orderKeys, bctxItem.bctx);
      bctxItemMap[bctxKey] = bctxItem;
    });
    return bctxItemMap;
  },

  backFillBctxInUserPerPieceLineMap(
    bctxDetails: TEventBctxDetails,
    ppLineMap: TUserActualsDB['dailyCache']['ppLineMap'],
    subject: string,
    shift: string,
    modifiedBctxKey: string,
    orderKeys: TBCTXKeysWithoutSize[],
  ) {
    const bctxKey = modifiedBctxKey;
    let ppLineMapBctxDetailsBctxKeyMap: TBctxKeyToBctxItemWithSizeMap = {};
    const orderedBctxDetails = BCTXComputationHelper.getOrderedBctx(
      bctxDetails.businessContextDetailsWithoutSize,
      orderKeys,
    );
    if (ppLineMap[subject] && ppLineMap[subject].lineInfo[shift]) {
      ppLineMapBctxDetailsBctxKeyMap = PerPieceComputationHelper.getPerPieceLineMapBctxKeyMapper(
        ppLineMap[subject].lineInfo[shift].bctx,
        orderKeys,
      );
      if (ppLineMapBctxDetailsBctxKeyMap[bctxKey]) {
        const isSizeAvailable = ppLineMapBctxDetailsBctxKeyMap[bctxKey].size.find((size) => size === bctxDetails.size);
        if (isSizeAvailable) {
          return ppLineMap;
        }
      }
    }

    ppLineMap = produce(ppLineMap, (draft) => {
      if (!draft[subject]) {
        draft[subject] = {
          lineInfo: {
            [shift]: {
              bctx: [
                {
                  bctx: orderedBctxDetails,
                  size: [bctxDetails.size],
                },
              ],
              clientWork: BCTXComputationHelper.createInitialClientWork(),
            },
          },
          ps: [],
          selectedBctxInfo: DEFAULT_SELECTED_BCTX_INFO,
        };
      } else if (!draft[subject].lineInfo) {
        draft[subject].lineInfo = {
          [shift]: {
            bctx: [
              {
                bctx: orderedBctxDetails,
                size: [bctxDetails.size],
              },
            ],
            clientWork: BCTXComputationHelper.createInitialClientWork(),
          },
        };
      } else if (!draft[subject].lineInfo[shift]) {
        draft[subject].lineInfo[shift] = {
          bctx: [
            {
              bctx: orderedBctxDetails,
              size: [bctxDetails.size],
            },
          ],
          clientWork: BCTXComputationHelper.createInitialClientWork(),
        };
      } else {
        ppLineMapBctxDetailsBctxKeyMap = PerPieceComputationHelper.getPerPieceLineMapBctxKeyMapper(
          draft[subject].lineInfo[shift].bctx,
          orderKeys,
        );
        if (ppLineMapBctxDetailsBctxKeyMap[bctxKey]) {
          const isSizeAvailable = ppLineMapBctxDetailsBctxKeyMap[bctxKey].size.find(
            (size) => size === bctxDetails.size,
          );
          if (!isSizeAvailable) {
            ppLineMapBctxDetailsBctxKeyMap[bctxKey].size.push(bctxDetails.size);
          }
        } else {
          draft[subject].lineInfo[shift].bctx.push({
            bctx: orderedBctxDetails,
            size: [bctxDetails.size],
          });
        }
      }
    });
    return ppLineMap;
  },
  updateActualsForFailedBulkEventsWithFilteredSingleEventsForPerPiece(
    bgDB: TBackgroundEventsSyncingDB,
    failedBulkEvents: TBulkEvent[],
    filteredSingleEventStatus: TSingleCountEvent['eventBackgroundMetadata']['status'][],
  ) {
    failedBulkEvents.forEach((bulkEvent) => {
      if (bulkEvent.type === 'FORWARD_BULK_EVENT' || bulkEvent.type === 'UNDO_BULK_EVENT') {
        const stationDetails = bulkEvent.metadata.stationDetails;
        const direction = -1;
        bulkEvent.events.forEach((event) => {
          if (filteredSingleEventStatus.find((status) => status === event.eventBackgroundMetadata.status)) {
            const bctxDetails = event.eventInfo.bctxDetails;

            bgDB = produce(bgDB, (currentDB) => {
              if (!currentDB.dailyCache.ppLineMap[stationDetails.subject.subject]) {
                currentDB.dailyCache.ppLineMap = {
                  [stationDetails.subject.subject]: {
                    lineInfo: {
                      [stationDetails.shift.id]: {
                        clientWork: BCTXComputationHelper.createInitialClientWork(),
                      },
                    },
                  },
                };
              }
              const client_work =
                currentDB.dailyCache.ppLineMap[stationDetails.subject.subject].lineInfo[stationDetails.shift.id]
                  .clientWork;
              switch (event.actionType) {
                case 'DEFECTIVE':
                case 'UNDO_DEFECTIVE':
                  if (event.subType === 'DEFECTIVE' || event.subType === 'UNDO_DEFECTIVE') {
                    client_work.DEFECTIVE.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    client_work.IN_PROGRESS.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    client_work.TO_BE_RECTIFIED.actuals +=
                      bctxDetails.count * (event.actionType === 'UNDO_DEFECTIVE' ? -1 : 1) * direction;
                    return;
                  } else return;
                case 'CHECK_IN':
                case 'UNDO_CHECK_IN':
                  client_work.CHECK_IN.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_CHECK_IN' ? -1 : 1) * direction;
                  return;
                case 'FTT':
                case 'UNDO_FTT':
                  client_work.FTT.actuals += bctxDetails.count * (event.actionType === 'UNDO_FTT' ? -1 : 1) * direction;
                  return;
                case 'REJECTED':
                case 'UNDO_REJECTED':
                  client_work.REJECTED.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED' ? -1 : 1) * direction;
                  return;
                case 'REWORKED_FROM_DEFECTIVE':
                case 'UNDO_REWORKED_FROM_DEFECTIVE': {
                  const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                  client_work.REWORKED_FROM_DEFECTIVE.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.TO_BE_RECTIFIED.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  if (isToday) {
                    client_work.IN_PROGRESS.actuals -=
                      bctxDetails.count * (event.actionType === 'UNDO_REWORKED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  }
                  return;
                }
                case 'REJECTED_FROM_DEFECTIVE':
                case 'UNDO_REJECTED_FROM_DEFECTIVE': {
                  const isToday = BCTXComputationHelper.isToday(event.sourceDefectiveCreatedTime);
                  client_work.REJECTED_FROM_DEFECTIVE.actuals +=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  client_work.TO_BE_RECTIFIED.actuals -=
                    bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  if (isToday) {
                    client_work.IN_PROGRESS.actuals -=
                      bctxDetails.count * (event.actionType === 'UNDO_REJECTED_FROM_DEFECTIVE' ? -1 : 1) * direction;
                  }
                  return;
                }
              }
            });
          }
        });
      }
    });
    return bgDB;
  },
  updateCurrentDBWithWipMapAndShiftMapForTheNewBctxSizes: (
    currentDB: TUserActualsDB,
    selectedStationIdAndShiftId: TSelectedStationIdAndShiftId,
    newBctxForPOSplit: TNewBctxItemInPOSplit,
    newBctxSizes: string[],
    orderKeys: TBCTXKeysWithoutSize[],
  ): TUserActualsDB => {
    let ppLineMapBctxDetailsBctxKeyMap: TBctxKeyToBctxItemWithSizeMap = {};
    const newBctxKey = newBctxForPOSplit.bctx.bctxKey;
    const orderedBctxDetails = BCTXComputationHelper.getOrderedBctx(newBctxForPOSplit.bctx.list, orderKeys);
    const modifiedBctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(orderKeys, newBctxForPOSplit.bctx.list);
    const { stationId, shiftId } = selectedStationIdAndShiftId;

    if (currentDB.dailyCache.ppLineMap[stationId] && currentDB.dailyCache.ppLineMap[stationId].lineInfo[shiftId]) {
      ppLineMapBctxDetailsBctxKeyMap = PerPieceComputationHelper.getPerPieceLineMapBctxKeyMapper(
        currentDB.dailyCache.ppLineMap[stationId].lineInfo[shiftId].bctx,
        orderKeys,
      );
      if (ppLineMapBctxDetailsBctxKeyMap[modifiedBctxKey]) {
        const isSizeAvailable = ppLineMapBctxDetailsBctxKeyMap[modifiedBctxKey].size.find(
          (size) => size === newBctxSizes[0],
        );
        if (isSizeAvailable || !newBctxSizes[0]) {
          return currentDB;
        }
      }
    }

    return produce(currentDB, (draft) => {
      if (!draft.dailyCache.ppLineMap[stationId]) {
        draft.dailyCache.ppLineMap[stationId] = {
          lineInfo: {
            [shiftId]: {
              bctx: [
                {
                  bctx: orderedBctxDetails,
                  size: newBctxSizes.map((size) => size),
                },
              ],
              clientWork: BCTXComputationHelper.createInitialClientWork(),
            },
          },
          ps: [],
          selectedBctxInfo: DEFAULT_SELECTED_BCTX_INFO,
        };
      } else if (!draft.dailyCache.ppLineMap[stationId].lineInfo) {
        draft.dailyCache.ppLineMap[stationId].lineInfo = {
          [shiftId]: {
            bctx: [
              {
                bctx: orderedBctxDetails,
                size: newBctxSizes.map((size) => size),
              },
            ],
            clientWork: BCTXComputationHelper.createInitialClientWork(),
          },
        };
      } else if (!draft.dailyCache.ppLineMap[stationId].lineInfo[shiftId]) {
        draft.dailyCache.ppLineMap[stationId].lineInfo[shiftId] = {
          bctx: [
            {
              bctx: orderedBctxDetails,
              size: newBctxSizes.map((size) => size),
            },
          ],
          clientWork: BCTXComputationHelper.createInitialClientWork(),
        };
      } else {
        ppLineMapBctxDetailsBctxKeyMap = PerPieceComputationHelper.getPerPieceLineMapBctxKeyMapper(
          draft.dailyCache.ppLineMap[stationId].lineInfo[shiftId].bctx,
          orderKeys,
        );
        if (ppLineMapBctxDetailsBctxKeyMap[newBctxKey]) {
          newBctxSizes.forEach((size) => {
            const isSizeAvailable = ppLineMapBctxDetailsBctxKeyMap[newBctxKey].size.find((size) => size === size);
            if (!isSizeAvailable) {
              ppLineMapBctxDetailsBctxKeyMap[newBctxKey].size.push(size);
            }
          });
        } else {
          draft.dailyCache.ppLineMap[stationId].lineInfo[shiftId].bctx.push({
            bctx: orderedBctxDetails,
            size: newBctxSizes.map((size) => size),
          });
        }
      }
    });
  },
};
